"use client";

import {
  LoaderCircle,
  MessageCircle,
  X,
} from "lucide-react";
import {
  FormEvent,
  useState,
} from "react";

import {
  getAnalyticsSessionId,
  getMarketplaceAttribution,
} from "@/lib/analytics";
import type {
  BusinessConfig,
} from "@/lib/business-config";
import {
  createPublicLead,
} from "@/lib/leads";
import {
  normalizeWhatsapp,
} from "@/lib/order";
import type {
  LeadSource,
} from "@/types/lead";
import type {
  PublicProduct,
} from "@/types/store";

type LeadCaptureModalProps = {
  companySlug: string;
  companyName: string;
  whatsapp: string;
  product: PublicProduct;
  businessConfig: BusinessConfig;
  source?: LeadSource;
  onClose: () => void;
  onWhatsapp: () => void;
};

function buildWhatsappUrl(
  whatsapp: string,
  companyName: string,
  product: PublicProduct,
  businessConfig: BusinessConfig,
  name: string,
  message: string,
) {
  const number = normalizeWhatsapp(whatsapp);

  const reference =
    businessConfig.businessType === "real_estate" &&
    product.reference_code
      ? ` — Ref. ${product.reference_code}`
      : "";

  const parts = [
    `Olá! Meu nome é ${name}.`,
    `Encontrei a empresa ${companyName} no fornecedor.net e tenho interesse no ${businessConfig.itemSingularLower} "${product.name}"${reference}.`,
  ];

  if (message.trim()) {
    parts.push(`Mensagem: ${message.trim()}`);
  }

  return (
    `https://wa.me/${number}?text=` +
    encodeURIComponent(parts.join("\n\n"))
  );
}

export default function LeadCaptureModal({
  companySlug,
  companyName,
  whatsapp,
  product,
  businessConfig,
  source = "product",
  onClose,
  onWhatsapp,
}: LeadCaptureModalProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] =
    useState(false);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    const cleanName = name.trim();
    const cleanPhone = phone.trim();

    if (!cleanName || !cleanPhone) {
      setError(
        "Informe seu nome e WhatsApp para continuar.",
      );
      return;
    }

    setSubmitting(true);
    setError("");

    /*
     * Reserva a nova aba enquanto ainda estamos
     * dentro da ação direta do usuário. Isso evita
     * bloqueio de popup depois da chamada assíncrona.
     */
    const whatsappWindow =
      window.open("about:blank", "_blank");

    try {
      await createPublicLead(companySlug, {
        product_id: product.id,
        name: cleanName,
        phone: cleanPhone,
        email: email.trim() || undefined,
        message: message.trim() || undefined,
        source,
        ...(source === "marketplace" ? { metadata: getMarketplaceAttribution() } : {}),
        session_id: getAnalyticsSessionId(),
      });

      const whatsappUrl = buildWhatsappUrl(
        whatsapp,
        companyName,
        product,
        businessConfig,
        cleanName,
        message,
      );

      onWhatsapp();

      if (whatsappWindow) {
        whatsappWindow.opener = null;
        whatsappWindow.location.href =
          whatsappUrl;
      } else {
        window.location.href = whatsappUrl;
      }

      onClose();
    } catch (err) {
      if (whatsappWindow) {
        whatsappWindow.close();
      }

      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível registrar seu interesse. Tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const title =
    businessConfig.businessType === "services"
      ? "Solicitar orçamento"
      : `Tenho interesse neste ${businessConfig.itemSingularLower}`;

  const buttonLabel =
    businessConfig.businessType === "services"
      ? "Solicitar orçamento pelo WhatsApp"
      : "Continuar pelo WhatsApp";

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-5">
      <button
        type="button"
        aria-label="Fechar formulário"
        className="absolute inset-0"
        onClick={onClose}
      />

      <section className="relative z-10 w-full rounded-t-[2rem] bg-white p-5 shadow-2xl sm:max-w-lg sm:rounded-[2rem] sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">
              {businessConfig.metricsRelationshipLabel}
            </p>

            <h2 className="mt-1 text-xl font-black text-slate-950">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-500">
              Deixe seus dados para que {companyName} saiba quem está entrando em contato.
            </p>
          </div>

          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>

        <form
          className="mt-6 space-y-4"
          onSubmit={handleSubmit}
        >
          <div>
            <label className="text-sm font-bold text-slate-700">
              Nome *
            </label>

            <input
              type="text"
              value={name}
              onChange={(event) =>
                setName(event.target.value)
              }
              autoComplete="name"
              maxLength={160}
              className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
              placeholder="Seu nome"
            />
          </div>

          <div>
            <label className="text-sm font-bold text-slate-700">
              WhatsApp *
            </label>

            <input
              type="tel"
              value={phone}
              onChange={(event) =>
                setPhone(event.target.value)
              }
              autoComplete="tel"
              maxLength={30}
              className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
              placeholder="(11) 99999-9999"
            />
          </div>

          <div>
            <label className="text-sm font-bold text-slate-700">
              E-mail
            </label>

            <input
              type="email"
              value={email}
              onChange={(event) =>
                setEmail(event.target.value)
              }
              autoComplete="email"
              maxLength={254}
              className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
              placeholder="voce@email.com"
            />
          </div>

          <div>
            <label className="text-sm font-bold text-slate-700">
              Mensagem
            </label>

            <textarea
              value={message}
              onChange={(event) =>
                setMessage(event.target.value)
              }
              maxLength={1000}
              rows={3}
              className="mt-2 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
              placeholder={`Escreva uma dúvida ou informação sobre este ${businessConfig.itemSingularLower}.`}
            />
          </div>

          {error && (
            <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 text-sm font-black text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {submitting ? (
              <>
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
                Registrando interesse...
              </>
            ) : (
              <>
                <MessageCircle size={18} />
                {buttonLabel}
              </>
            )}
          </button>
        </form>
      </section>
    </div>
  );
}
